import { type FileKind, zipDirectoryLocation, zipEntries } from '@felix/domain';
import type ExcelJS from 'exceljs';
import { TOKENS } from '../../tokens.js';

/**
 * Önizleyicinin çekirdeği (plan M8a Task 15): motor seçimi, `Range` okuyucu, sandbox'lı iframe
 * belgesi ve XLSX tablosu. DOM'a ve kütüphanelere dokunan çizim `viewer-engines.ts`'tedir;
 * buradakiler Node'da sınanır.
 */

/**
 * DOCX ve XLSX önizlemesi bütün dosyayı ister (ZIP): bu boyutun üstünde önizlenmez, indirme
 * önerilir. PDF ve resim `Range` ile okunur; boyut sınırı yoktur.
 */
export const MAX_INLINE_PREVIEW_BYTES = 64 * 1024 * 1024;

/**
 * XLSX'in sınırı daha düşük (inceleme F11): sıkıştırılmış birkaç MB milyonlarca hücre olabilir;
 * exceljs bütün kitabı arayüz iş parçacığında açar. 16 MiB'ın üstü indirilerek açılır.
 */
export const MAX_INLINE_XLSX_BYTES = 16 * 1024 * 1024;

/** Sayfa başına çizilen en çok satır ve sütun (inceleme F11); fazlası için not ve indirme. */
export const MAX_SHEET_ROWS = 2000;
export const MAX_SHEET_COLUMNS = 100;
/** Bütün sayfalarda çizilen en çok hücre. */
const MAX_WORKBOOK_CELLS = 400_000;

/**
 * DOCX/XLSX'in açılmış boyutunun sınırı (inceleme M8a3-R21): docx-preview (jszip) ve exceljs
 * parçaları arayüz iş parçacığında bütünüyle belleğe açar. Birkaç MB'lık dosya GB'lara açılan
 * parça taşıyabilir (sıkıştırma bombası); sekme ya da gemi uygulamasının penceresi çöker.
 */
export const MAX_PREVIEW_UNPACKED_BYTES = 256 * 1024 * 1024;
export const MAX_PREVIEW_PART_BYTES = 128 * 1024 * 1024;
/** Bu açılmış boyutun üstünde 1:100'den yüksek sıkıştırma oranı önizlenmez. */
const RATIO_CHECK_BYTES = 32 * 1024 * 1024;
const MAX_PREVIEW_RATIO = 100;

/**
 * Motordan önce ZIP merkezi dizini okunur; bildirilen açılmış boyut sınırı aşarsa dosya burada
 * önizlenmez (indirip açılır). ZIP64'ün bilinmeyen boyutu (`0xFFFFFFFF`) aşmış sayılır. Bozuk
 * ZIP motorun kendi hatasına kalır. Bildirilen boyut yalan olabilir: gerçek açma ölçümü sunucuda
 * ayrı iştir (ADR 0035 Ek, açık konu).
 */
export function archiveTooLargeToPreview(data: Uint8Array): boolean {
  let entries: ReturnType<typeof zipEntries>;
  try {
    const location = zipDirectoryLocation(
      data.subarray(Math.max(0, data.length - 65_557)),
      data.length,
    );
    entries = zipEntries(data.subarray(location.offset, location.offset + location.size));
  } catch {
    return false;
  }
  let total = 0;
  for (const entry of entries) {
    if (entry.uncompressedSize > MAX_PREVIEW_PART_BYTES) return true;
    total += entry.uncompressedSize;
  }
  if (total > MAX_PREVIEW_UNPACKED_BYTES) return true;
  return total > RATIO_CHECK_BYTES && total > data.length * MAX_PREVIEW_RATIO;
}

export type PreviewEngine = 'pdf' | 'docx' | 'xlsx' | 'image' | 'too-large';

export function previewEngine(kind: FileKind, size: number): PreviewEngine {
  if (kind === 'pdf') return 'pdf';
  if (size > MAX_INLINE_PREVIEW_BYTES) return 'too-large';
  if (kind === 'xlsx' && size > MAX_INLINE_XLSX_BYTES) return 'too-large';
  if (kind === 'docx' || kind === 'xlsx') return kind;
  return 'image';
}

/**
 * Basılı kopyaya giden parçalar: DOCX'te sayfa, XLSX'te sayfa kabı. XLSX'te yalnız tablo
 * alınsaydı satır/sütun sınırı notu kâğıda girmez, kesik sayfa tam kopya gibi görünürdü
 * (inceleme M8a3-R32).
 */
export const PRINT_PART_SELECTOR = { docx: 'section.docx', xlsx: '[data-sheet]' } as const;

/** Yakınlaştırma yüzdesi dile göre: TR "%100", EN "100%" (inceleme M8a3-R36). */
export function zoomText(scale: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
    scale,
  );
}

/**
 * Önizleme hazır değilken baskının hatası (inceleme M8a3-R12): çok büyük ya da açılamayan dosya
 * burada hiç basılamaz (indirip açın); yüklenirken bir an sonra yeniden denenir.
 */
export function previewPrintError(
  engine: PreviewEngine,
  status: 'loading' | 'ready' | 'error',
): 'PREVIEW_UNAVAILABLE' | 'PREVIEW_NOT_READY' {
  return engine === 'too-large' || status === 'error' ? 'PREVIEW_UNAVAILABLE' : 'PREVIEW_NOT_READY';
}

/** Hata: `code` arayüzde `documents.errors.<code>` ile çevrilir. */
export class ViewerError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(code);
    this.name = 'ViewerError';
    this.code = code;
  }
}

export function parseContentRange(
  header: string | null | undefined,
): { start: number; end: number; total: number } | null {
  const match = /^bytes (\d+)-(\d+)\/(\d+)$/.exec(header?.trim() ?? '');
  if (!match) return null;
  return { start: Number(match[1]), end: Number(match[2]), total: Number(match[3]) };
}

export type FetchLike = (
  url: string,
  init?: { headers?: Record<string, string>; credentials?: 'same-origin'; signal?: AbortSignal },
) => Promise<Response>;

async function failure(response: Response): Promise<ViewerError> {
  let code = `HTTP_${response.status}`;
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body?.error === 'string') code = body.error;
  } catch {
    // Gövde JSON değil: HTTP kodu kalır
  }
  return new ViewerError(code);
}

/**
 * Dosya ucunun bildirdiği revizyon durumu (`x-felix-revision-state`; inceleme M8a3-R15): resim
 * `<img>` ile, çok büyük dosya hiç okunmaz; durum tek baytlık `Range` isteğiyle alınır, gövde
 * okunmaz. Okunamazsa `null` (koruma detaya kalır).
 */
export async function probeServedState(
  url: string,
  fetchImpl: FetchLike = (input, init) => fetch(input, init),
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const response = await fetchImpl(url, {
      credentials: 'same-origin',
      headers: { Range: 'bytes=0-0' },
      ...(signal ? { signal } : {}),
    });
    const state = response.ok ? response.headers.get('x-felix-revision-state') : null;
    await response.body?.cancel().catch(() => undefined);
    return state;
  } catch {
    return null;
  }
}

/**
 * Dosyayı parça parça okur (`Range`): 300 MB'lık PDF bütünüyle belleğe alınmaz (plan Task 15).
 * Gemide adres `felix-file://`'dır; ana süreç `Range`'i gemi sunucusuna geçirir (Task 16).
 * Sunucu `Range`'i yok sayıp bütün dosyayı verirse yalnız küçük dosya kabul edilir.
 */
export function createRangeReader(
  url: string,
  fetchImpl: FetchLike = (input, init) => fetch(input, init),
  options: { chunkSize?: number; signal?: AbortSignal } = {},
) {
  const chunkSize = options.chunkSize ?? 256 * 1024;
  const request = (range?: string) =>
    fetchImpl(url, {
      credentials: 'same-origin',
      ...(range ? { headers: { Range: range } } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  return {
    chunkSize,
    /** İlk parça, dosyanın boyu ve revizyonun durumu (`x-felix-revision-state`). */
    async open(): Promise<{ length: number; initialData: Uint8Array; state: string | null }> {
      const response = await request(`bytes=0-${chunkSize - 1}`);
      if (!response.ok) throw await failure(response);
      const state = response.headers.get('x-felix-revision-state');
      if (response.status === 206) {
        const range = parseContentRange(response.headers.get('content-range'));
        const data = new Uint8Array(await response.arrayBuffer());
        return { length: range?.total ?? data.byteLength, initialData: data, state };
      }
      const declared = Number(response.headers.get('content-length') ?? Number.NaN);
      if (Number.isFinite(declared) && declared > MAX_INLINE_PREVIEW_BYTES) {
        await response.body?.cancel();
        throw new ViewerError('RANGE_NOT_SUPPORTED');
      }
      const data = new Uint8Array(await response.arrayBuffer());
      return { length: data.byteLength, initialData: data, state };
    },
    /** `[begin, end)` aralığı. */
    async read(begin: number, end: number): Promise<Uint8Array> {
      const response = await request(`bytes=${begin}-${end - 1}`);
      if (!response.ok) throw await failure(response);
      const data = new Uint8Array(await response.arrayBuffer());
      if (response.status === 206) return data;
      // Sunucu Range'i yok saydı: istenen dilim kesilir
      return data.subarray(begin, end);
    },
    /** Bütün dosya (DOCX/XLSX; boyutu `previewEngine` sınırlar). */
    async whole(): Promise<{ data: ArrayBuffer; state: string | null }> {
      const response = await request();
      if (!response.ok) throw await failure(response);
      return {
        data: await response.arrayBuffer(),
        state: response.headers.get('x-felix-revision-state'),
      };
    },
  };
}

/**
 * Okumayı geçici hatada artan beklemeyle yeniden dener (inceleme F1): gemi sunucusu yeniden
 * başlarken ya da LAN kısa süre koparken parça okuması kaybolmaz. Denemeler biterse ya da iptal
 * edilirse hata yukarı iletilir; pdf.js'e hata bildirmenin yolu yoktur, çağıran belgeyi kapatır.
 */
export async function readWithRetry<T>(
  read: () => Promise<T>,
  options: {
    delays?: readonly number[];
    signal?: AbortSignal | undefined;
    sleep?: (ms: number) => Promise<void>;
  } = {},
): Promise<T> {
  const delays = options.delays ?? [500, 1500, 4000];
  const sleep =
    options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await read();
    } catch (error) {
      const delay = delays[attempt];
      if (options.signal?.aborted || delay === undefined) throw error;
      await sleep(delay);
      if (options.signal?.aborted) throw error;
    }
  }
}

/**
 * Yalnız son işin sonucu (inceleme F12): her tuşta başlayan belge içi aramadan geç biten eskisi
 * sayacı ve vurguyu ezmez. `cancel` hepsini bayatlatır (arama kapandı).
 */
export function createLatestGate() {
  let current = 0;
  return {
    begin(): number {
      current += 1;
      return current;
    },
    isCurrent(token: number): boolean {
      return token === current;
    },
    cancel(): void {
      current += 1;
    },
  };
}

/**
 * Bekleyen işlerin hepsi bitince (başarılı ya da değil) açılan kapı (inceleme F9): bütün PDF'ler
 * tek pdf.js worker'ını paylaşır; önceki belgenin `destroy()`'u bitmeden yeni belge açılırsa
 * pdf.js "worker is being destroyed" hatası verir.
 *
 * `within`: kapı en çok `ms` kadar beklenir; süre dolarsa `false`. pdf.js'in `destroy()`'u hiç
 * bitmeyebilir: büyük PDF'in kaynakları parça parça yüklenirken belge kapatılırsa worker'daki
 * görev, iptalden sonra yeniden istediği parçayı bekleyip asılı kalır. Kapı sonsuza dek kapalı
 * kalırsa sonraki bütün önizlemeler "Loading preview…"da kalır; çağıran yeni worker'a geçer ve
 * `reset` ile asılı işleri bırakır.
 */
export function createSettleGate() {
  let pending: Promise<void> = Promise.resolve();
  return {
    track(work: Promise<unknown>): void {
      const settled = work.then(
        () => undefined,
        () => undefined,
      );
      pending = Promise.all([pending, settled]).then(() => undefined);
    },
    idle(): Promise<void> {
      return pending;
    },
    within(
      ms: number,
      sleep: (ms: number) => Promise<void> = (wait) =>
        new Promise<void>((resolve) => setTimeout(resolve, wait)),
    ): Promise<boolean> {
      return Promise.race([pending.then(() => true), sleep(ms).then(() => false)]);
    },
    reset(): void {
      pending = Promise.resolve();
    },
  };
}

/**
 * DOCX ve XLSX çıktısı bu iframe'de çizilir (ADR 0028 #35, #37): betik yok (`allow-scripts`
 * verilmez), aynı köken (üst pencere DOM'a erişir, belge içi arama ve baskı). Dış kaynak
 * yüklenmez: belge CSP'si yalnız gömülü resim ve fontlara izin verir.
 */
export const FRAME_SANDBOX = 'allow-same-origin';

const FRAME_CSP = [
  "default-src 'none'",
  "script-src 'none'",
  "style-src 'unsafe-inline'",
  'img-src data: blob:',
  'font-src data:',
].join('; ');

export function frameDocument(): string {
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">`,
    '<style>',
    `html,body{margin:0;padding:0;background:${TOKENS['viewer-canvas']};}`,
    `body{color:${TOKENS['navy-900']};font-family:Carlito,Calibri,"Segoe UI",Arial,sans-serif;}`,
    '</style></head><body></body></html>',
  ].join('');
}

/** Basılacak her sayfa ya da tablo bu kabın içindedir (`[data-print-part]`). */
export const PRINT_PART_SCOPE = '[data-print-root] [data-print-part]';

/** CSSOM kuralı (`CSSRule`): yalnız kullanılan alanlar; Node testinde de kurulur. */
export interface CssRuleLike {
  /** `CSSRule.type`: 1 stil, 5 `@font-face`; diğerleri (`@page`, `@import`, `@media` …) atılır. */
  type: number;
  cssText: string;
  selectorText?: string;
  style?: {
    length: number;
    item(index: number): string;
    getPropertyValue(name: string): string;
    getPropertyPriority(name: string): string;
  };
}

/** Seçici listesini en dış virgüllerden böler (parantez, köşeli ayraç, tırnak ve kaçış içinde değil). */
export function splitSelectorList(text: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '\\') {
      i += 1;
      continue;
    }
    if (quote) {
      if (char === quote) quote = null;
    } else if (char === '"' || char === "'") quote = char;
    else if (char === '(' || char === '[') depth += 1;
    else if (char === ')' || char === ']') depth = Math.max(0, depth - 1);
    else if (char === ',' && depth === 0) {
      parts.push(text.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(text.slice(start));
  return parts.map((part) => part.trim()).filter((part) => part !== '');
}

/** Dış kaynak isteyen değer: `data:` dışında adres (`url()`, `image-set()` …). */
function loadsExternal(value: string): boolean {
  const lower = value.toLowerCase();
  if (/(image-set|image|src|element|cross-fade)\(/.test(lower)) return true;
  for (const match of lower.matchAll(/url\(\s*(['"]?)\s*([^'")\s]*)/g)) {
    if (!(match[2] ?? '').startsWith('data:')) return true;
  }
  return false;
}

/** `@font-face`'in `font-family` tanımlayıcısı (ilk; tırnaklı ya da tırnaksız). */
const FONT_FAMILY_DESCRIPTOR =
  /(\bfont-family\s*:\s*)("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|[^;}]+)/i;

/** Aile adının karşılaştırma biçimi: tırnaksız, küçük harf, boşluklar tek. */
function familyKey(name: string): string {
  return name
    .trim()
    .replace(/^(["'])(.*)\1$/s, '$2')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function fontFaceFamily(cssText: string): string | null {
  const match = FONT_FAMILY_DESCRIPTOR.exec(cssText.replace(/^[^{]*\{/, ''));
  return match?.[2] ? familyKey(match[2]) : null;
}

/** Aile listesindeki gömülü aileleri yeniden adlandırılmış adlarıyla değiştirir. */
function renameFamilies(value: string, families: ReadonlyMap<string, string>): string {
  if (families.size === 0) return value;
  return splitSelectorList(value)
    .map((family) => families.get(familyKey(family)) ?? family)
    .join(', ');
}

/**
 * DOCX önizlemesinin CSS'i basılı kopyaya (inceleme SEC-1): docx-preview stil kimliğini ve yazı
 * tipi adını kaçışsız CSS'e yazar; Word dosyası istediği kuralı taşıyabilir. Bu yüzden iframe'in
 * `<style>`'ları olduğu gibi kopyalanmaz; CSSOM'dan yeniden kurulur:
 *  - yalnız stil kuralları ve gömülü (`data:`) `@font-face` kalır; `@page`, `@import`, `@media`
 *    ve diğer bütün @-kuralları atılır (altlığı yalnız sistem yazar; ADR 0028 #30);
 *  - her seçici `scope`'un alt öğesine kapsanır: kural basılı parçanın kendisini (filigran
 *    `::after`'ı), altlığı ya da uygulamayı seçemez;
 *  - `position: fixed/sticky` (her sayfada yinelenip altlığı örtebilir) ve dış kaynak isteyen
 *    bildirimler atılır; iç içe kurallar alınmaz.
 * Sistem altlığı ve filigranı bundan sonra `!important` ile eklenir (`mountPrintRoot`).
 */
export function scopedPrintCss(rules: Iterable<CssRuleLike>, scope: string): string {
  const out: string[] = [];
  // Gömülü yazı tipinin ailesi belge geneli bir addır, kapsanamaz: DOCX "Arial" adıyla boş glifli
  // yazı tipi gömerse sistem filigranını ve altlığını gölgelerdi. Aile tekil adla yeniden
  // adlandırılır; belgenin bildirimleri o ada çevrilir (inceleme M8a3-R1; ADR 0028 #30)
  const families = new Map<string, string>();
  const list = [...rules];
  for (const rule of list) {
    if (rule.type !== 5) continue;
    const family = fontFaceFamily(rule.cssText);
    if (family !== null && !families.has(family)) {
      families.set(family, `"felix-docx-font-${families.size}"`);
    }
  }
  for (const rule of list) {
    if (rule.type === 5) {
      const family = fontFaceFamily(rule.cssText);
      const renamed = family === null ? undefined : families.get(family);
      if (
        renamed &&
        /^@font-face\s*\{/i.test(rule.cssText) &&
        !loadsExternal(rule.cssText.replace(/^[^{]*/, ''))
      ) {
        out.push(rule.cssText.replace(FONT_FAMILY_DESCRIPTOR, `$1${renamed}`));
      }
      continue;
    }
    if (rule.type !== 1 || !rule.style || !rule.selectorText) continue;
    const declarations: string[] = [];
    for (let i = 0; i < rule.style.length; i += 1) {
      const name = rule.style.item(i);
      const value = rule.style.getPropertyValue(name).trim();
      if (!name || value === '') continue;
      if (name === 'position' && /^(fixed|sticky)$/i.test(value)) continue;
      if (loadsExternal(value)) continue;
      const important = rule.style.getPropertyPriority(name) === 'important' ? '!important' : '';
      const font = name === 'font-family' || (name.startsWith('--') && /font/i.test(name));
      declarations.push(`${name}:${font ? renameFamilies(value, families) : value}${important}`);
    }
    if (declarations.length === 0) continue;
    const selectors = splitSelectorList(rule.selectorText).map((s) => `${scope} ${s}`);
    if (selectors.length === 0) continue;
    out.push(`${selectors.join(',')}{${declarations.join(';')}}`);
  }
  return out.join('\n');
}

/** Salt okunur sayfa: hücreler metin olarak, birleşik hücreler `colspan`/`rowspan` ile. */
export interface SheetModel {
  name: string;
  /** Çizilen sütun sayısı (sınırla). */
  columns: number;
  /** Sınır aşıldıysa sayfanın gerçek satır ve sütun sayısı; aşılmadıysa `null`. */
  truncated: { rows: number; columns: number } | null;
  /** Sütun genişliği (karakter; Excel birimi); yoksa varsayılan. */
  widths: (number | null)[];
  rows: {
    number: number;
    cells: { column: number; text: string; colspan: number; rowspan: number; bold: boolean }[];
  }[];
}

const escapeHtml = (text: string) =>
  text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');

/** Hücrenin gösterilen metni: formülde son sonuç, zengin metinde düz metin. */
function cellText(cell: ExcelJS.Cell): string {
  const value = cell.value;
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    if ('richText' in value) return value.richText.map((part) => part.text).join('');
    if ('result' in value) return value.result === undefined ? '' : String(value.result);
    if ('text' in value) return String(value.text);
    if ('error' in value) return String(value.error);
  }
  return cell.text ?? String(value);
}

/** Çalışma kitabının sayfaları (görünür olanlar), birleşik hücrelerle. */
export function sheetsFromWorkbook(workbook: ExcelJS.Workbook): SheetModel[] {
  const sheets: SheetModel[] = [];
  let budget = MAX_WORKBOOK_CELLS;
  workbook.eachSheet((sheet) => {
    if (sheet.state && sheet.state !== 'visible') return;
    const allColumns = Math.max(sheet.columnCount, 1);
    const allRows = Math.max(sheet.rowCount, 1);
    // Sınır: sayfa başına satır ve sütun, kitap başına hücre (inceleme F11)
    const columns = Math.min(allColumns, MAX_SHEET_COLUMNS);
    const lastRow = Math.min(allRows, MAX_SHEET_ROWS, Math.max(0, Math.floor(budget / columns)));
    budget -= lastRow * columns;
    const truncated =
      allRows > lastRow || allColumns > columns ? { rows: allRows, columns: allColumns } : null;
    const covered = new Set<string>();
    const rows: SheetModel['rows'] = [];
    for (let r = 1; r <= lastRow; r += 1) {
      const row = sheet.getRow(r);
      const cells: SheetModel['rows'][number]['cells'] = [];
      for (let c = 1; c <= columns; c += 1) {
        if (covered.has(`${r}:${c}`)) continue;
        const cell = row.getCell(c);
        let colspan = 1;
        let rowspan = 1;
        if (cell.isMerged && cell.master === cell) {
          // Birleşik alanın sınırı: aynı ana hücreyi gösteren komşular
          while (c + colspan <= columns && row.getCell(c + colspan).master === cell) colspan += 1;
          while (r + rowspan <= lastRow && sheet.getRow(r + rowspan).getCell(c).master === cell) {
            rowspan += 1;
          }
          for (let dr = 0; dr < rowspan; dr += 1) {
            for (let dc = 0; dc < colspan; dc += 1) {
              if (dr || dc) covered.add(`${r + dr}:${c + dc}`);
            }
          }
        }
        cells.push({
          column: c,
          text: cellText(cell),
          colspan,
          rowspan,
          bold: Boolean(cell.font?.bold),
        });
      }
      rows.push({ number: r, cells });
    }
    const widths = Array.from({ length: columns }, (_, i) => sheet.getColumn(i + 1).width ?? null);
    sheets.push({ name: sheet.name, columns, truncated, widths, rows });
  });
  return sheets;
}

const columnName = (index: number) => {
  let name = '';
  for (let n = index; n > 0; n = Math.floor((n - 1) / 26)) {
    name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
  }
  return name;
};

/**
 * Sayfanın salt okunur tablosu (iframe'e yazılır): sütun harfleri, satır numaraları; metin
 * kaçışlıdır, belge kodu çalışmaz (ADR 0028 #35). Hiçbir sütun kesilmez (#34 madde 5).
 */
export function sheetTableHtml(sheet: SheetModel, truncatedNote?: string): string {
  const head = Array.from(
    { length: sheet.columns },
    (_, i) => `<th scope="col">${columnName(i + 1)}</th>`,
  ).join('');
  const cols = sheet.widths
    .map((width) => `<col style="width:${Math.round((width ?? 9) * 7 + 10)}px">`)
    .join('');
  const body = sheet.rows
    .map((row) => {
      const cells = row.cells
        .map((cell) => {
          const span = [
            cell.colspan > 1 ? ` colspan="${cell.colspan}"` : '',
            cell.rowspan > 1 ? ` rowspan="${cell.rowspan}"` : '',
            cell.bold ? ' class="b"' : '',
          ].join('');
          return `<td${span}>${escapeHtml(cell.text)}</td>`;
        })
        .join('');
      return `<tr><th scope="row">${row.number}</th>${cells}</tr>`;
    })
    .join('');
  // Sınır aşıldıysa tablonun üstünde not: "ilk N satır; tamamı için indirin" (inceleme F11)
  const note =
    sheet.truncated && truncatedNote ? `<p class="note">${escapeHtml(truncatedNote)}</p>` : '';
  return `${note}<table class="sheet"><colgroup><col class="rowhead">${cols}</colgroup><thead><tr><th></th>${head}</tr></thead><tbody>${body}</tbody></table>`;
}
